// src/hooks/VoteContext.jsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import dayjs from "dayjs";

import { getAllVotes, getUserBallots } from "../services/voteServices";
import { useUser } from "../pages/Home/UserContext";

const VoteContext = createContext(null);

/**
 * Normalise allowed_roles :
 *
 * null
 * []
 * "[1, 2, 3]"
 * ["1", "2", "3"]
 * "1,2,3"
 *
 * vers number[]
 */
const parseAllowedRoles = (value) => {
  if (value == null || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map((roleId) => Number(roleId))
      .filter(Number.isFinite);
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);

      if (Array.isArray(parsedValue)) {
        return parsedValue
          .map((roleId) => Number(roleId))
          .filter(Number.isFinite);
      }
    } catch {
      return value
        .split(",")
        .map((roleId) => Number(roleId.trim()))
        .filter(Number.isFinite);
    }
  }

  return [];
};

const isVoteEnabled = (vote) => {
  const activeValue = vote?.is_active;

  return (
    activeValue === true ||
    activeValue === 1 ||
    activeValue === "1"
  );
};

const isVoteCurrentlyOpen = (vote) => {
  if (!vote || !isVoteEnabled(vote)) {
    return false;
  }

  const startDate = dayjs(vote.start_time);
  const endDate = dayjs(vote.end_time);
  const now = dayjs();

  if (!startDate.isValid() || !endDate.isValid()) {
    return false;
  }

  const hasStarted =
    now.isAfter(startDate) || now.isSame(startDate);

  const hasNotEnded =
    now.isBefore(endDate) || now.isSame(endDate);

  return hasStarted && hasNotEnded;
};

const canUserSeeVote = (vote, userRoleId) => {
  const allowedRoles = parseAllowedRoles(vote?.allowed_roles);

  // Aucun rôle défini : vote visible par tous les utilisateurs connectés.
  if (allowedRoles.length === 0) {
    return true;
  }

  if (!Number.isFinite(userRoleId)) {
    return false;
  }

  return allowedRoles.includes(userRoleId);
};

const getBallotVoteId = (ballot) => {
  return ballot?.vote_id ?? ballot?.voteId ?? null;
};

export const VoteProvider = ({ children }) => {
  const { user } = useUser();

  const userId = user?.userId;
  const userRoleId = Number(user?.role_id);

  const requestIdRef = useRef(0);

  const [pendingVoteCount, setPendingVoteCount] = useState(0);
  const [pendingVotesLoading, setPendingVotesLoading] =
    useState(false);
  const [pendingVotesError, setPendingVotesError] =
    useState(null);

  /**
   * Calcule le nombre de votes actifs non effectués à partir
   * de données déjà chargées.
   */
  const calculatePendingVoteCount = useCallback(
    (votes = [], ballots = []) => {
      if (!userId) {
        return 0;
      }

      const answeredVoteIds = new Set(
        (Array.isArray(ballots) ? ballots : [])
          .map(getBallotVoteId)
          .filter((voteId) => voteId != null)
          .map((voteId) => String(voteId)),
      );

      return (Array.isArray(votes) ? votes : []).filter(
        (vote) => {
          const voteId = vote?.id;

          if (voteId == null) {
            return false;
          }

          const isOpen = isVoteCurrentlyOpen(vote);
          const isVisible = canUserSeeVote(
            vote,
            userRoleId,
          );
          const hasAlreadyVoted = answeredVoteIds.has(
            String(voteId),
          );

          return isOpen && isVisible && !hasAlreadyVoted;
        },
      ).length;
    },
    [userId, userRoleId],
  );

  /**
   * Permet à une page ayant déjà chargé les votes et bulletins
   * de synchroniser le compteur sans refaire deux appels API.
   */
  const syncPendingVotes = useCallback(
    (votes = [], ballots = []) => {
      const count = calculatePendingVoteCount(
        votes,
        ballots,
      );

      setPendingVoteCount(count);
      setPendingVotesError(null);

      return count;
    },
    [calculatePendingVoteCount],
  );

  /**
   * Recharge directement les données depuis l'API.
   */
  const refreshPendingVotes = useCallback(async () => {
    if (!userId) {
      setPendingVoteCount(0);
      setPendingVotesLoading(false);
      setPendingVotesError(null);

      return 0;
    }

    const currentRequestId = ++requestIdRef.current;

    setPendingVotesLoading(true);

    try {
      const [voteData, ballotData] = await Promise.all([
        getAllVotes(),
        getUserBallots(userId),
      ]);

      // Une autre requête plus récente a été lancée.
      if (currentRequestId !== requestIdRef.current) {
        return null;
      }

      const count = calculatePendingVoteCount(
        Array.isArray(voteData) ? voteData : [],
        Array.isArray(ballotData) ? ballotData : [],
      );

      setPendingVoteCount(count);
      setPendingVotesError(null);

      return count;
    } catch (error) {
      if (currentRequestId !== requestIdRef.current) {
        return null;
      }

      console.error(
        "Erreur lors du chargement des votes en attente :",
        error,
      );

      setPendingVoteCount(0);
      setPendingVotesError(error);

      return 0;
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setPendingVotesLoading(false);
      }
    }
  }, [
    userId,
    calculatePendingVoteCount,
  ]);

  useEffect(() => {
    if (!userId) {
      requestIdRef.current += 1;

      setPendingVoteCount(0);
      setPendingVotesLoading(false);
      setPendingVotesError(null);

      return undefined;
    }

    refreshPendingVotes();

    // Actualisation périodique pour prendre en compte :
    // - un nouveau vote ;
    // - le début d'un vote ;
    // - la clôture d'un vote.
    const intervalId = window.setInterval(() => {
      refreshPendingVotes();
    }, 60_000);

    const handleWindowFocus = () => {
      refreshPendingVotes();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshPendingVotes();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );

    return () => {
      window.clearInterval(intervalId);

      window.removeEventListener(
        "focus",
        handleWindowFocus,
      );

      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [
    userId,
    userRoleId,
    refreshPendingVotes,
  ]);

  const contextValue = useMemo(
    () => ({
      pendingVoteCount,
      pendingVotesLoading,
      pendingVotesError,
      refreshPendingVotes,
      syncPendingVotes,
      setPendingVoteCount,
    }),
    [
      pendingVoteCount,
      pendingVotesLoading,
      pendingVotesError,
      refreshPendingVotes,
      syncPendingVotes,
    ],
  );

  return (
    <VoteContext.Provider value={contextValue}>
      {children}
    </VoteContext.Provider>
  );
};

export const useVote = () => {
  const context = useContext(VoteContext);

  if (!context) {
    throw new Error(
      "useVote doit être utilisé à l'intérieur de VoteProvider.",
    );
  }

  return context;
};